/**
 * Sawyer 14 early-clockin-gate (E1) — the "Early clock-in" card in the Time
 * clock section of Punch records. Owner or manager sets how many minutes
 * before a published shift a staffer may clock in without approval, or turns
 * it off (NULL, the default for every tenant).
 *
 * States: loading (input + Save disabled, no pill: AC32), off, on, invalid
 * (inline sentence, Save disabled, no request: AC2), saving, save error
 * (server sentence inline, draft kept: AC20). Rendered by
 * TimeclockAdminSection only when the server's settings carry the field
 * (AC21), so it never appears against an older server.
 */
import { useEffect, useState } from 'react';
import { adminTimeclockApi, type TimeclockSettingsView } from '../../lib/api.js';
import './early-clockin.css';

export const EARLY_ALLOWANCE_INVALID = 'Enter a whole number of minutes from 0 to 240.';

/** Whole minutes 0..240, or null when the draft is not one. */
export function parseAllowance(draft: string): number | null {
  const t = draft.trim();
  if (!/^\d{1,3}$/.test(t)) return null;
  const n = Number(t);
  return n >= 0 && n <= 240 ? n : null;
}

export type EarlyClockInCardProps = {
  apiUrl: string;
  /** null while the settings request is in flight. */
  settings: TimeclockSettingsView | null;
  onSaved: (settings: TimeclockSettingsView, notice: string) => void;
};

export function EarlyClockInCard({ apiUrl, settings, onSaved }: EarlyClockInCardProps): JSX.Element {
  const current = settings?.early_clock_in_allowance_minutes ?? null;
  const [draft, setDraft] = useState('');
  const [touched, setTouched] = useState(false);
  const [pending, setPending] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Seed the draft from the server value once it arrives / changes.
  useEffect(() => {
    setDraft(current === null ? '' : String(current));
    setTouched(false);
  }, [current]);

  const loading = settings === null;
  const parsed = parseAllowance(draft);
  const invalid = touched && parsed === null;

  const save = async (value: number | null): Promise<void> => {
    setPending(true);
    setSaveError(null);
    const res = await adminTimeclockApi.updateSettings(apiUrl, {
      earlyClockInAllowanceMinutes: value,
    });
    setPending(false);
    if (res.error !== undefined) {
      setSaveError(res.error);
      return;
    }
    if (res.data?.settings) {
      const next = res.data.settings.early_clock_in_allowance_minutes ?? null;
      onSaved(
        res.data.settings,
        next === null
          ? 'Early clock-in approval is off. Staff clock in normally at any time.'
          : `Early clock-in saved. More than ${next} ${next === 1 ? 'minute' : 'minutes'} before a published shift now needs a manager's approval.`,
      );
    }
  };

  return (
    <div className="ferro-tc-card" data-testid="tc-early-card">
      <div className="ferro-tc-card__head">
        <h3 className="ferro-tc-card__title">Early clock-in</h3>
        {loading ? null : (
          <span
            className={`ferro-status-pill ${current === null ? 'ferro-status-pill--pending' : 'ferro-status-pill--success'}`}
            data-testid="tc-early-pill"
          >
            {current === null ? 'Off' : `On, ${current} ${current === 1 ? 'minute' : 'minutes'}`}
          </span>
        )}
      </div>
      <p className="ferro-tc-card__hint">
        Staff who clock in more than this many minutes before their published shift are still
        clocked in, but their paid time starts at the shift time until a manager approves it on
        this page. Staff with no published shift that day clock in normally.
      </p>
      <form
        className="ferro-tc-early-card__row"
        onSubmit={(e) => {
          e.preventDefault();
          setTouched(true);
          if (parsed === null) return;
          void save(parsed);
        }}
      >
        <label className="ferro-field ferro-tc-early-card__field">
          <span className="ferro-field__label">Minutes before a shift that need no approval</span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={240}
            step={1}
            className="ferro-field__input"
            value={draft}
            disabled={loading || pending}
            aria-invalid={invalid}
            aria-describedby={invalid ? 'tc-early-invalid' : undefined}
            data-testid="tc-early-input"
            onChange={(e) => {
              setDraft(e.target.value);
              setTouched(true);
              setSaveError(null);
            }}
          />
        </label>
        <div className="ferro-tc-card__actions">
          <button
            type="submit"
            className="ferro-btn ferro-btn--outline"
            disabled={loading || pending || parsed === null}
            data-testid="tc-early-save"
          >
            {pending ? 'Saving…' : 'Save'}
          </button>
          {current !== null ? (
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              disabled={loading || pending}
              data-testid="tc-early-off"
              onClick={() => void save(null)}
            >
              Turn off
            </button>
          ) : null}
        </div>
      </form>
      {invalid ? (
        <p className="ferro-form__error" id="tc-early-invalid" role="alert" data-testid="tc-early-invalid">
          {EARLY_ALLOWANCE_INVALID}
        </p>
      ) : null}
      {saveError ? (
        <p className="ferro-form__error" role="alert" data-testid="tc-early-save-error">
          {saveError}
        </p>
      ) : null}
    </div>
  );
}

export default EarlyClockInCard;
